// src/App.js
import React, { useEffect, useState, useCallback } from 'react'
import { flushSync } from 'react-dom'
import { supabase } from './supabaseClient'
import TrainDoorTransition from './components/TrainDoorTransition'
import Landing from './components/Landing'
import StationEntry from './components/StationEntry'
import Journal from './components/Journal'
import Splash from './components/Splash'
import InstallPrompt from './components/InstallPrompt'
import AuthPage from './components/AuthPage'
import Dashboard from './components/Dashboard'
import './App.css'
import './cities.css'

const SPLASH_KEY = 'magic_splash_v2'

// QA hatch: ?city=nyc&station=18 routes directly into that station, bypassing auth + progress gates
const _qp = new URLSearchParams(window.location.search)
const _qaCity = _qp.get('city')
const _qaStation = parseInt(_qp.get('station'), 10)
const QA_OVERRIDE = _qaCity && ['nyc','paris','london','milan','berlin','madrid'].includes(_qaCity) && _qaStation >= 1 && _qaStation <= 28
  ? { city: _qaCity, station: _qaStation }
  : null

export default function App() {
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showSplash, setShowSplash] = useState(false)
  const [city, setCity] = useState(QA_OVERRIDE?.city ?? null)
  const [view, setView] = useState(QA_OVERRIDE ? 'station' : 'landing') // 'landing' | 'station' | 'journal'
  const [theme, setTheme] = useState(() => {
    // If user has manually set a preference, respect it
    const saved = localStorage.getItem('magic_theme_manual')
    if (saved) return saved
    // Otherwise default to light — sun-based will update once geolocation resolves
    return 'sunny'
  })
  const [showDashboard, setShowDashboard] = useState(false)
  const [dashboardTab, setDashboardTab] = useState('overview')
  const [showMoments, setShowMoments] = useState(false)
  // cityProgress: { nyc: 2, paris: 0, london: 0, milan: 0 }
  // means nyc has 2 completed stations, so next station is 3
  const [cityProgress, setCityProgress] = useState({})
  const [round2Progress, setRound2Progress] = useState({})
  const [trainDoor, setTrainDoor] = useState(null)

  // Load progress for all cities once after session is available
  const loadProgress = useCallback(async (userId) => {
    const { data, error } = await supabase
      .from('journal_entries')
      .select('city, day_number, gratitudes')
      .eq('user_id', userId)
    if (!error && data) {
      const progress = {}
      data.forEach(row => {
        const hasSomething = row.gratitudes?.some(g => g?.trim())
        if (hasSomething) {
          if (!progress[row.city] || row.day_number > progress[row.city]) {
            progress[row.city] = row.day_number
          }
        }
      })
      setCityProgress(progress)
    }
  }, [])

  const loadRound2Progress = useCallback(async (userId) => {
    const { data, error } = await supabase
      .from('round_2_progress')
      .select('city, current_station')
      .eq('user_id', userId)
    if (!error && data) {
      const progress = {}
      data.forEach(row => { progress[row.city] = row.current_station })
      setRound2Progress(progress)
    }
  }, [])

  useEffect(() => {
    // onAuthStateChange fires immediately with INITIAL_SESSION — use it as primary auth source
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session)
      setLoading(false)
      if (session) loadProgress(session.user.id)
      if (session) loadRound2Progress(session.user.id)
      // Show splash only once per browser session, on first load
      if (event === 'INITIAL_SESSION') {
        const seen = sessionStorage.getItem(SPLASH_KEY)
        if (!seen) {
          setShowSplash(true)
          sessionStorage.setItem(SPLASH_KEY, '1')
        }
      }
    })
    return () => subscription.unsubscribe()
  }, [loadProgress, loadRound2Progress])

  // Apply theme to DOM
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
  }, [theme])

  // Sun-based auto theme — runs once on mount
  useEffect(() => {
    const manual = localStorage.getItem('magic_theme_manual')
    if (manual) return // user has overridden, don't auto-switch

    if (!navigator.geolocation) return

    navigator.geolocation.getCurrentPosition(pos => {
      const { latitude: lat, longitude: lng } = pos.coords
      // Simple sunrise/sunset calculation (no API needed)
      const now = new Date()
      const d = now
      const J = 2451545.0 + (d - new Date('2000-01-01T12:00:00Z')) / 86400000
      const n = Math.ceil(J - 2451545.0 + 0.0008)
      const Js = n - lng / 360
      const M = (357.5291 + 0.98560028 * Js) % 360
      const C = 1.9148 * Math.sin(M * Math.PI/180) + 0.02 * Math.sin(2 * M * Math.PI/180)
      const lam = (M + C + 180 + 102.9372) % 360
      const Jt = 2451545.0 + Js + 0.0053 * Math.sin(M * Math.PI/180) - 0.0069 * Math.sin(2 * lam * Math.PI/180)
      const decl = Math.asin(Math.sin(lam * Math.PI/180) * Math.sin(23.4397 * Math.PI/180)) * 180/Math.PI
      const cosH = (Math.sin(-0.833 * Math.PI/180) - Math.sin(lat * Math.PI/180) * Math.sin(decl * Math.PI/180)) / (Math.cos(lat * Math.PI/180) * Math.cos(decl * Math.PI/180))
      if (cosH < -1 || cosH > 1) return // polar regions
      const H = Math.acos(cosH) * 180/Math.PI
      const Jrise = Jt - H / 360
      const Jset = Jt + H / 360
      const toDate = j => new Date((j - 2440587.5) * 86400000)
      const sunrise = toDate(Jrise)
      const sunset = toDate(Jset)
      const isDaytime = now >= sunrise && now <= sunset
      setTheme(isDaytime ? 'sunny' : 'dark')
    }, () => {
      // Geolocation denied — fall back to simple hour-based
      const h = new Date().getHours()
      setTheme(h >= 6 && h < 20 ? 'sunny' : 'dark')
    }, { timeout: 5000, maximumAge: 3600000 })
  }, [])

  // Manual toggle — saves preference, overrides sun logic
  const toggleTheme = () => {
    const next = theme === 'dark' ? 'sunny' : 'dark'
    setTheme(next)
    localStorage.setItem('magic_theme_manual', next)
  }

  // Next station number for a city (1-based, max 28)
  const getNextStation = (c) => Math.min((cityProgress[c] || 0) + 1, 28)

  const handleSelectCity = (selectedCity) => {
    setTrainDoor(selectedCity)
  }

  const handleTrainDoorComplete = (selectedCity) => {
    setCity(selectedCity)
    setView('station')
    setTimeout(() => setTrainDoor(null), 50)
  }

  const handleBeginJourney = () => setView('journal')
  const handleNavigateToCity = (selectedCity) => {
    flushSync(() => {
      setCity(selectedCity)
      setView('station')
      setShowDashboard(false)
      setDashboardTab('overview')
    })
  }
  const handleBackToStation = () => setView('station')
  const handleBackToLanding = () => { setCity(null); setView('landing') }

  // After journal saves an entry, refresh progress
  const handleProgressUpdate = useCallback((updatedCity, stationNum) => {
    setCityProgress(prev => ({
      ...prev,
      [updatedCity]: Math.max(prev[updatedCity] || 0, stationNum)
    }))
  }, [])

  const handleRound2ProgressUpdate = useCallback((updatedCity, stationNum) => {
    setRound2Progress(prev => ({
      ...prev,
      [updatedCity]: Math.max(prev[updatedCity] || 0, stationNum)
    }))
  }, [])

  // App-level train door overlay — sits above all views
  const trainDoorOverlay = trainDoor ? (
    <TrainDoorTransition
      targetCity={trainDoor}
      onComplete={() => handleTrainDoorComplete(trainDoor)}
    />
  ) : null

  if (loading) return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ fontFamily: 'var(--fn)', fontSize: 'clamp(3.5rem, 12vw, 6rem)', fontWeight: 900, letterSpacing: '-3px', lineHeight: 1, color: 'var(--text)' }}>The Magic</div>
    </div>
  )

  if (showSplash) return (
    <Splash onDone={() => setShowSplash(false)} theme={theme} />
  )

  if (!session && !QA_OVERRIDE) return <>{trainDoorOverlay}<AuthPage theme={theme} toggleTheme={toggleTheme} /></>

  if (showMoments) return (
    <Dashboard
      session={session}
      city={city || 'nyc'}
      onBack={() => setShowMoments(false)}
      theme={theme}
      toggleTheme={toggleTheme}
      initialTab="moments"
    />
  )

  if (showDashboard) return (
    <Dashboard
      session={session}
      city={city || 'nyc'}
      onBack={() => setShowDashboard(false)}
      onNavigateToCity={handleNavigateToCity}
      theme={theme}
      toggleTheme={toggleTheme}
      initialTab={dashboardTab}
    />
  )

  if (view === 'station' && city) return (
    <>
      {trainDoorOverlay}
      <StationEntry
        city={city}
        session={session}
        theme={theme}
        toggleTheme={toggleTheme}
        nextStation={QA_OVERRIDE?.station ?? getNextStation(city)}
        cityProgress={cityProgress[city] || 0}
        onBegin={handleBeginJourney}
        onBack={handleBackToLanding}
        onShowLines={() => { setDashboardTab('journey'); setShowDashboard(true) }}
        skipMap={!!QA_OVERRIDE}
      />
    </>
  )

  if (view === 'journal' && city) {
    const qaAlreadyDone = QA_OVERRIDE && session &&
      QA_OVERRIDE.station <= (cityProgress[QA_OVERRIDE.city] || 0)
    const journalMode = qaAlreadyDone ? 'practice' : 'first_pass'
    return (
      <Journal
        session={session}
        city={city}
        theme={theme}
        toggleTheme={toggleTheme}
        onBack={handleBackToStation}
        onShowDashboard={() => setShowDashboard(true)}
        onShowLines={() => { setDashboardTab('journey'); setShowDashboard(true) }}
        onShowMoments={() => setShowMoments(true)}
        onProgressUpdate={handleProgressUpdate}
        onRound2ProgressUpdate={handleRound2ProgressUpdate}
        journalMode={journalMode}
        qaStation={QA_OVERRIDE?.station}
      />
    )
  }

  return (
    <>
      {trainDoorOverlay}
      <InstallPrompt />
      <Landing
        session={session}
        theme={theme}
        toggleTheme={toggleTheme}
        onSelectCity={handleSelectCity}
        cityProgress={cityProgress}
        getNextStation={getNextStation}
        onShowDashboard={() => setShowDashboard(true)}
        onShowMoments={() => setShowMoments(true)}
      />
    </>
  )
}
